import {
  FilterOperationType,
  PurchaseOrderQuery,
  PurchaseOrderQueryV2,
} from 'src/WebApiController';

type CatalogEventId = {
  viagVirtId: string;
  performerId: number | null;
  venueId: number;
};

/**
 * Builds the filter query for V2 purchase order search/summary requests.
 *
 * Only `eventOrMappingIds` falls back to the catalog event window (`allEventIds`)
 * when the user has no explicit selection — this keeps V2 scoped to the same
 * catalog window as V1 without over-constraining results.
 *
 * The catalog fallback is intentionally suppressed when:
 * - The user is searching by direct IDs (`purchaseOrderIds` or `eventIdsOverride`):
 *   stale catalog events from a prior filter session would act as an unintended
 *   AND constraint, causing operations like "Not In" on PO IDs to return zero results.
 * - Any catalog-level filter uses NOT IN (`countryCodes`, `stateProvinceIds`,
 *   `cityIds`, `performerIds`, `venueIds`, `topLevelCategories`, `parentCategories`,
 *   `eventStatuses`): the catalog window is a subset of events matching whatever was
 *   previously browsed, so injecting those events as a positive `ViagogoEventId IN [...]`
 *   constraint directly contradicts the NOT IN filter in OpenSearch (e.g. "Country NOT IN
 *   [GB]" + all catalog events are GB events → `ViagogoEventId IN [GB events] AND
 *   VenueCountryCode NOT IN [GB]` = 0 results; same pattern for Genre/EventState NOT IN).
 *
 * `performerIds` and `venueIds` must NOT fall back to `allEventIds`.  The catalog
 * counts API returns POS-internal IDs (e.g. negative virtual venue IDs like
 * `-1455734` for unmapped events), but OpenSearch stores `CatalogData.VenueId = 0`
 * and `CatalogData.PerformerId = null` for those same unmapped events.  Passing the
 * POS-internal IDs as AND filters to the QB would incorrectly exclude POs whose OS
 * documents carry `0`/`null` for those fields, causing V1/V2 count mismatches.
 * The `eventOrMappingIds` filter alone is sufficient to scope results correctly.
 *
 * @param moddedFilterQuery - The base filter query (with eventOrMappingIds /
 *   performerIds / venueIds already nulled-out by the hook's `useMemo`).
 * @param filterQuery - The raw filter from the FilterQueryContext (carries
 *   explicit user selections, or null when the user hasn't filtered).
 * @param allEventIds - The catalog event window provided by CatalogDataContext.
 */
export function buildV2FilterQuery(
  moddedFilterQuery: Partial<PurchaseOrderQuery | PurchaseOrderQueryV2>,
  filterQuery: Pick<
    PurchaseOrderQuery | PurchaseOrderQueryV2,
    | 'eventOrMappingIds'
    | 'performerIds'
    | 'venueIds'
    | 'purchaseOrderIds'
    | 'eventIdsOverride'
    | 'countryCodes'
    | 'stateProvinceIds'
    | 'cityIds'
    | 'topLevelCategories'
    | 'parentCategories'
    | 'eventStatuses'
  > & { operations?: PurchaseOrderQueryV2['operations'] },
  allEventIds: CatalogEventId[] | null | undefined
): Partial<PurchaseOrderQueryV2> {
  // When searching by direct IDs, do not fall back to the catalog event window.
  // Stale catalog events from a prior filter session would act as an unintended
  // AND constraint, causing operations like "Not In" on PO IDs to return zero results.
  const hasDirectIdFilter = Boolean(
    filterQuery.purchaseOrderIds?.length || filterQuery.eventIdsOverride?.length
  );

  // When a catalog-level filter uses NOT IN, injecting the current catalog window as
  // positive event IDs creates contradictory OpenSearch directives (SAPI-4273).
  const { operations: ops } = filterQuery;
  const hasCatalogNotInFilter = Boolean(
    (filterQuery.countryCodes?.length &&
      ops?.countryCodesOp === FilterOperationType.NotIn) ||
    (filterQuery.stateProvinceIds?.length &&
      ops?.stateProvinceIdsOp === FilterOperationType.NotIn) ||
    (filterQuery.cityIds?.length &&
      ops?.cityIdsOp === FilterOperationType.NotIn) ||
    (filterQuery.performerIds?.length &&
      ops?.performerIdsOp === FilterOperationType.NotIn) ||
    (filterQuery.venueIds?.length &&
      ops?.venueIdsOp === FilterOperationType.NotIn) ||
    (filterQuery.topLevelCategories?.length &&
      ops?.topLevelCategoriesOp === FilterOperationType.NotIn) ||
    (filterQuery.parentCategories?.length &&
      ops?.parentCategoriesOp === FilterOperationType.NotIn) ||
    (filterQuery.eventStatuses?.length &&
      ops?.eventStatusesOp === FilterOperationType.NotIn)
  );

  return {
    ...moddedFilterQuery,
    eventOrMappingIds:
      hasDirectIdFilter || hasCatalogNotInFilter
        ? []
        : (filterQuery.eventOrMappingIds ??
          (allEventIds ?? []).map((ev) => ev.viagVirtId)),
    performerIds: filterQuery.performerIds ?? null,
    venueIds: filterQuery.venueIds ?? null,
  };
}
